import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { setAppStore, useAppStore, type IAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import getOrFetchQuery from '@/utils/query/getOrFetchQuery';

import getProjectCredits from './api/getProjectCredits';

const selector = (store: IAppStore) => ({
  setCredits: store.setCredits,
  flowId: store.selectedPreset?.flowId,
});

/**
 * Owns the credits lifecycle:
 *   - clears credits on logout
 *   - fetches credits whenever a preset becomes selected (any code path —
 *     preset mutation, deeplink handler, etc.) and the user is authorized.
 *
 * The fetch reacts to selectedPreset.flowId, so any path that writes
 * selectedPreset triggers it — no per-path duplication.
 */
const useCreditsSync = () => {
  const { isAuthorized } = useAuth();
  const { setCredits, flowId } = useAppStore(selector);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthorized) setCredits(0);
  }, [isAuthorized, setCredits]);

  useEffect(() => {
    if (!isAuthorized || !flowId) {
      setAppStore({ isCreditsLoading: false });
      return;
    }

    let cancelled = false;
    setAppStore({ isCreditsLoading: true });

    getOrFetchQuery<number | null>({
      key: ['projectCredits', flowId],
      queryClient,
      fetcher: () => getProjectCredits({ webhookId: flowId }),
    })
      .then((projectCreditsCount) => {
        if (cancelled) return;
        setCredits(projectCreditsCount ?? 0);
      })
      .finally(() => {
        if (cancelled) return;
        setAppStore({ isCreditsLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthorized, flowId, queryClient, setCredits]);
};

export default useCreditsSync;
